import { Component, Inject, OnDestroy, OnInit, signal, computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { ActivatedRoute, Router } from '@angular/router';
import { Order, TownshipPublicSettings, VoucherGroup } from '../interfaces';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getEnvName, getTermsUrl, getTownshipImage, updateTheme } from '../globals';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../app.component';
import { PublicFunctionsService } from '../services/public-functions.service';
import { CustomValidators } from 'angular-custom-validators';
import { DOCUMENT, NgClass } from '@angular/common';
import { MatFormField, MatLabel, MatError, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { Subscription } from 'rxjs';
@Component({
  selector: 'app-buy-vouchers',
  templateUrl: './buy-vouchers.component.html',
  styleUrls: ['./buy-vouchers.component.scss'],
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatButton,
    MatRadioGroup,
    MatRadioButton,
    MatHint,
    CdkTextareaAutosize,
    MatCheckbox,
    MatProgressSpinner,
    TranslatePipe,
    MatIcon,
  ],
})
export class BuyVouchersComponent implements OnInit, OnDestroy {
  readonly db = db;
  readonly functions = functions;
  // @ViewChild('stepper') private stepper: MatStepper;
  env = environment;
  iframeError = signal(false);
  campaignInactive = signal(false);
  awaitingResponse = signal(false);
  townshipId = signal<string>(undefined);
  voucherGroupId = signal<string>(undefined);
  township = signal<TownshipPublicSettings>(undefined);
  voucherGroup = signal<VoucherGroup>(undefined);
  formStep1: UntypedFormGroup;
  formStep2: UntypedFormGroup;
  formStep3: UntypedFormGroup;
  formStep4: UntypedFormGroup;
  availableVouchers = signal<number>(undefined);
  termsRows = signal<number>(undefined);
  headerImg = signal<string>(undefined);
  showHeaderImg = signal<boolean>(undefined);
  termsUrl = signal<string>(getTermsUrl('general'));
  privacyUrl = signal<string>(getTermsUrl('privacy'));
  step = signal<number>(1);
  extraStepExists = signal(false);
  // sisowBanks: any[] = [];

  envName = signal<any>({});
  townshipImage = signal<any>(undefined);

  // issuers: { id: string; name: string; imgPath: string }[] = [];
  showStep = signal<boolean>(undefined);

  formStep1Value!: Signal<any>;
  voucherTotal!: Signal<number>;
  giftWrappingTotal!: Signal<number>;
  totalPrice!: Signal<number>;

  private lastResolvedBilling = { postal: '', houseNumber: '' };
  private lastResolvedDelivery = { postal: '', houseNumber: '' };
  private readonly subscriptions = new Subscription();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private translate: TranslateService,
    private publicFunctionsService: PublicFunctionsService,
  ) {
    this.formStep1 = this.fb.group({
      amount: [, [Validators.required, Validators.min(1)]],
      value: [, [Validators.required, Validators.min(10), Validators.max(300)]],
      giftWrapping: [false],
    });
    this.formStep2 = this.fb.group({
      type: ['customer'],
      delivery: ['equalto'],
      name: ['', Validators.required],
      email: [, [Validators.required, Validators.email]],
      postal: ['', [Validators.required, CustomValidators.postal]],
      city: ['', Validators.required],
      street: ['', Validators.required],
      houseNumberAddition: ['', [Validators.pattern(/^[a-zA-Z0-9]*$/)]],
      houseNumber: ['', [Validators.required, CustomValidators.numberInput(false, false, 0)]],
      deliveryStreet: ['', Validators.required],
      deliveryHouseNumber: [
        '',
        [Validators.required, CustomValidators.numberInput(false, false, 0)],
      ],
      deliveryHouseNumberAddition: ['', [Validators.pattern(/^[a-zA-Z0-9]*$/)]],
      deliveryPostal: ['', [Validators.required, CustomValidators.postal]],
      deliveryCity: ['', Validators.required],
    });
    this.formStep3 = this.fb.group({
      personalMessage: [],
    });
    this.formStep4 = this.fb.group({
      terms: [false, Validators.requiredTrue],
    });

    this.formStep1Value = toSignal(this.formStep1.valueChanges, {
      initialValue: this.formStep1.value,
    });

    this.voucherTotal = computed(() => {
      const vg = this.voucherGroup();
      const f1 = this.formStep1Value();
      if (!vg || !f1) return 0;
      const price = vg.voucherPrice === 0 ? f1.value || 0 : vg.voucherPrice;
      return price * (f1.amount || 0);
    });

    this.giftWrappingTotal = computed(() => {
      const vg = this.voucherGroup();
      const f1 = this.formStep1Value();
      if (!vg || !f1 || !f1.giftWrapping || vg.sellType !== 'physical') return 0;
      return (vg.giftWrappingPrice || 0) * (f1.amount || 0);
    });

    this.totalPrice = computed(() => {
      const vg = this.voucherGroup();
      if (!vg) return 0;
      return this.voucherTotal() + (vg.administrationCost || 0) + this.giftWrappingTotal();
    });
  }

  ngOnInit() {
    this.subscriptions.add(
      this.route.params.subscribe((params) => {
        this.townshipId.set(params.townshipId);
        this.voucherGroupId.set(params.voucherGroupId);
        if (this.townshipId() && this.voucherGroupId()) {
          this.getInfo();
        } else {
          this.iframeError.set(true);
        }
      }),
    );

    this.translate.setDefaultLang(environment.language);
    this.translate.use(environment.language);

    this.formStep2.controls.type.valueChanges.subscribe((value) => {
      if (value === 'company') {
        this.formStep2.addControl('companyName', new UntypedFormControl('', Validators.required));
      } else if (this.formStep2.controls.companyName) {
        this.formStep2.removeControl('companyName');
      }
    });
    merge(
      this.formStep2.controls.houseNumber.valueChanges,
      this.formStep2.controls.postal.valueChanges,
    )
      .pipe(debounceTime(600))
      .subscribe(async () => {
        await this.getAddress();
      });
    merge(
      this.formStep2.controls.deliveryHouseNumber.valueChanges,
      this.formStep2.controls.deliveryPostal.valueChanges,
    )
      .pipe(debounceTime(600))
      .subscribe(async () => {
        await this.getDeliveryAddress();
      });

    const billingFields = ['street', 'houseNumber', 'houseNumberAddition', 'postal', 'city'];
    billingFields.forEach((field) => {
      this.formStep2.get(field).valueChanges.subscribe((value) => {
        if (this.formStep2.value.delivery === 'equalto') {
          const deliveryFieldName = 'delivery' + field.charAt(0).toUpperCase() + field.slice(1);
          this.formStep2.get(deliveryFieldName).patchValue(value, { emitEvent: false });
        }
      });
    });
    this.formStep2.controls.delivery.valueChanges.subscribe((value) => {
      const rawForm = this.formStep2.getRawValue();
      if (value === 'differentfrom') {
        this.formStep2.controls.deliveryStreet.enable();
        this.formStep2.controls.deliveryStreet.patchValue('');

        this.formStep2.controls.deliveryHouseNumber.enable();
        this.formStep2.controls.deliveryHouseNumber.patchValue('');

        this.formStep2.controls.deliveryHouseNumberAddition.enable();
        this.formStep2.controls.deliveryHouseNumberAddition.patchValue('');

        this.formStep2.controls.deliveryPostal.enable();
        this.formStep2.controls.deliveryPostal.patchValue('');

        this.formStep2.controls.deliveryCity.enable();
        this.formStep2.controls.deliveryCity.patchValue('');
      }
      if (value == 'equalto') {
        if (this.formStep2.controls.deliveryStreet.enabled) {
          this.formStep2.controls.deliveryStreet.disable();
        }
        if (this.formStep2.controls.deliveryHouseNumber.enabled) {
          this.formStep2.controls.deliveryHouseNumber.disable();
        }
        if (this.formStep2.controls.deliveryHouseNumberAddition.enabled) {
          this.formStep2.controls.deliveryHouseNumberAddition.disable();
        }
        if (this.formStep2.controls.deliveryPostal.enabled) {
          this.formStep2.controls.deliveryPostal.disable();
        }
        if (this.formStep2.controls.deliveryCity.enabled) {
          this.formStep2.controls.deliveryCity.disable();
        }
        if (
          this.formStep2.value.street !== rawForm.deliveryStreet ||
          this.formStep2.controls.deliveryStreet.enabled
        ) {
          this.formStep2.controls.deliveryStreet.patchValue(this.formStep2.getRawValue().street);
        }
        if (this.formStep2.value.houseNumber !== rawForm.deliveryHouseNumber) {
          this.formStep2.controls.deliveryHouseNumber.patchValue(this.formStep2.value.houseNumber, {
            emitEvent: false,
          });
        }
        if (this.formStep2.value.houseNumberAddition !== rawForm.deliveryHouseNumberAddition) {
          this.formStep2.controls.deliveryHouseNumberAddition.patchValue(
            this.formStep2.value.houseNumberAddition,
          );
        }
        if (this.formStep2.value.postal !== rawForm.deliveryPostal) {
          this.formStep2.controls.deliveryPostal.patchValue(this.formStep2.value.postal, {
            emitEvent: false,
          });
        }
        if (this.formStep2.value.city !== rawForm.deliveryCity) {
          this.formStep2.controls.deliveryCity.patchValue(this.formStep2.getRawValue().city);
        }
      }
    });
    this.formStep2.controls.delivery.setValue('equalto');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async getAddress() {
    this.formStep2.controls.city.disable({ emitEvent: false });
    this.formStep2.controls.street.disable({ emitEvent: false });
    this.formStep2.controls.postal.setErrors(null, { emitEvent: false });
    this.formStep2.controls.postal.updateValueAndValidity({ emitEvent: false });
    if (
      this.formStep2.value.postal &&
      this.formStep2.value.postal !== '' &&
      this.formStep2.controls.postal.valid &&
      this.formStep2.value.houseNumber &&
      this.formStep2.value.houseNumber !== ''
    ) {
      const postal: string = this.formStep2.value.postal.replace(/\s+/g, '');
      const houseNumber: string = this.formStep2.value.houseNumber.trim();
      if (
        this.lastResolvedBilling.postal === postal &&
        this.lastResolvedBilling.houseNumber === houseNumber
      ) {
        return;
      }
      try {
        const recaptchaToken = await this.publicFunctionsService.getRecaptchaToken('get_address');
        const callable = httpsCallable(this.functions, 'httpGetAddressWithApi', {
          timeout: 60000,
        });
        const result = await callable({
          postal: postal,
          houseNumber: houseNumber,
          recaptchaToken,
        });
        if (result.data['street'] && result.data['city']) {
          this.formStep2.controls.street.patchValue(result.data['street']);
          this.formStep2.controls.city.patchValue(result.data['city']);
          this.lastResolvedBilling = { postal, houseNumber };
        } else if (result.data['name'] === 'Error') {
          this.formStep2.controls.city.enable();
          this.formStep2.controls.street.enable();
          this.formStep2.controls.postal.setErrors({ apiError: true });
          this.lastResolvedBilling = { postal: '', houseNumber: '' };
        }
      } catch {
        this.formStep2.controls.city.enable();
        this.formStep2.controls.street.enable();
        this.formStep2.controls.postal.setErrors({ apiError: true });
        this.lastResolvedBilling = { postal: '', houseNumber: '' };
      }
    }
  }

  async getDeliveryAddress() {
    this.formStep2.controls.deliveryCity.disable({ emitEvent: false });
    this.formStep2.controls.deliveryStreet.disable({ emitEvent: false });
    this.formStep2.controls.deliveryPostal.setErrors(null, { emitEvent: false });
    this.formStep2.controls.deliveryPostal.updateValueAndValidity({
      emitEvent: false,
    });
    if (
      this.formStep2.value.deliveryPostal &&
      this.formStep2.value.deliveryPostal !== '' &&
      this.formStep2.controls.deliveryPostal.valid &&
      this.formStep2.value.deliveryHouseNumber &&
      this.formStep2.value.deliveryHouseNumber !== ''
    ) {
      const postal: string = this.formStep2.value.deliveryPostal.replace(/\s+/g, '');
      const houseNumber: string = this.formStep2.value.deliveryHouseNumber.trim();
      if (
        this.lastResolvedDelivery.postal === postal &&
        this.lastResolvedDelivery.houseNumber === houseNumber
      ) {
        return;
      }
      try {
        const recaptchaToken = await this.publicFunctionsService.getRecaptchaToken('get_address');
        const callable = httpsCallable(this.functions, 'httpGetAddressWithApi', {
          timeout: 60000,
        });
        const result = await callable({
          postal: postal,
          houseNumber: houseNumber,
          recaptchaToken,
        });
        if (result.data['street'] && result.data['city']) {
          this.formStep2.controls.deliveryStreet.patchValue(result.data['street']);
          this.formStep2.controls.deliveryCity.patchValue(result.data['city']);
          this.lastResolvedDelivery = { postal, houseNumber };
        } else if (result.data['name'] === 'Error') {
          this.formStep2.controls.deliveryCity.enable();
          this.formStep2.controls.deliveryStreet.enable();
          this.formStep2.controls.deliveryPostal.setErrors({ apiError: true });
          this.lastResolvedDelivery = { postal: '', houseNumber: '' };
        }
      } catch {
        this.formStep2.controls.deliveryCity.enable();
        this.formStep2.controls.deliveryStreet.enable();
        this.formStep2.controls.deliveryPostal.setErrors({ apiError: true });
        this.lastResolvedDelivery = { postal: '', houseNumber: '' };
      }
    }
  }

  getError(name, formstep = 'formStep2') {
    const field = this[formstep].get(name);
    if (field.touched || !field.pristine) {
      let error;
      if (field.hasError('required')) {
        error = 'required';
      }
      if (field.hasError('email')) {
        error = 'email';
      }
      if (field.hasError('number') || field.hasError('numberInput')) {
        error = name === 'houseNumber' || name === 'deliveryHouseNumber' ? 'houseNumber' : 'number';
      }
      if (field.hasError('pattern') || field.hasError('postalValidator')) {
        error = 'pattern';
      }
      if (
        field.hasError('pattern') &&
        (name === 'houseNumberAddition' || name === 'deliveryHouseNumberAddition')
      ) {
        error = 'addition';
      }
      if (field.hasError('minlength')) {
        error = 'phone';
      }
      if (field.hasError('emailsNeedToBeTheSame')) {
        error = 'emails_must_match';
      }
      if (error) {
        const res = this.translate.instant(`errors.${error}`) as string;
        return res;
      }
    }
    return '';
  }
  async getInfo() {
    this.township.set(
      (
        await getDoc(doc(this.db, `township/${this.townshipId()}/settings/public`))
      ).data() as TownshipPublicSettings,
    );

    this.townshipImage.set(getTownshipImage(this.township()));

    if (this.township()?.hideSource) {
      this.campaignInactive.set(true);
      return;
    }

    updateTheme({
      primary: this.township().primaryColor,
      secondary: this.township().accentColor,
    });
    this.envName.set(getEnvName(this.township().voucherPrefix));
    const voucherGroupData = (
      await getDoc(
        doc(this.db, `township/${this.townshipId()}/voucherGroups/${this.voucherGroupId()}`),
      )
    ).data() as VoucherGroup;
    if (voucherGroupData?.hidden) {
      this.campaignInactive.set(true);
      return;
    }
    this.voucherGroup.set(voucherGroupData);
    this.termsUrl.set(
      getTermsUrl('general', voucherGroupData?.termsURL, this.township()?.termsUrl),
    );
    if (this.voucherGroup().paymentProvider === 'ideal') {
      this.showStep.set(true);
      const recaptchaToken =
        await this.publicFunctionsService.getRecaptchaToken('ideal_get_access_token');
      const callable = httpsCallable(this.functions, 'idealGetAccessToken', {
        timeout: 60000,
      });
      const result = await callable({
        townshipId: this.townshipId(),
        recaptchaToken,
      });
      console.log('Accestoken result:', result.data);
    }
    onSnapshot(
      doc(this.db, `township/${this.townshipId()}/voucherGroups/${this.voucherGroupId()}`),
      (voucherGroupDoc) => {
        const voucherGroup = voucherGroupDoc.data() as VoucherGroup;
        voucherGroup.id = voucherGroupDoc.id;
        this.termsRows.set(5);
        console.log('voucherGroup', voucherGroup);
        if (!voucherGroup.hideHeaderImage) {
          this.showHeaderImg.set(true);
        } else {
          this.showHeaderImg.set(false);
        }

        if (voucherGroup.voucherPrice !== 0) {
          this.formStep1.removeControl('value');
        }

        if (voucherGroup.requestPhone) {
          this.formStep2.addControl(
            'phone',
            new UntypedFormControl('', [CustomValidators.phone, Validators.required]),
          );
        }

        console.log('voucherGroup', voucherGroup);

        if (voucherGroup.sellType == 'physical') {
          if (voucherGroup.personalisedNote) {
            this.extraStepExists.set(true);
          }
        }

        const reservedVouchers =
          (voucherGroup.reservedVouchers ?? 0) + (voucherGroup.toBeActivated ?? 0);

        this.availableVouchers.set(voucherGroup.statistics?.availableVouchers - reservedVouchers);

        console.log('this.availableVouchers', this.availableVouchers());
        if (this.availableVouchers() <= 0) {
          this.formStep1.disable();
          this.formStep2.disable();
          this.formStep3.disable();
          this.formStep4.disable();
        } else {
          this.formStep1.enable();
          this.formStep2.enable();
          this.formStep3.enable();
          this.formStep4.enable();
        }
        this.voucherGroup.set(voucherGroup);
        this.termsUrl.set(getTermsUrl('general', voucherGroup.termsURL, this.township()?.termsUrl));
      },
    );
  }
  getCurrencyString(number: number | undefined | null) {
    let currencyString: string;
    if (number === undefined || number === null || isNaN(number) || number === 0) {
      return '0,-';
    }
    if (!number.toString().includes('.')) {
      currencyString = `${number.toString()},-`;
    } else {
      currencyString = `${number.toFixed(2)}`;
      currencyString = currencyString.replace('.', ',');
    }
    return currencyString;
  }
  ceil(number) {
    return Math.ceil(number);
  }

  stepForward() {
    this.step.update((s) => s + 1);
  }

  stepBackward() {
    this.step.update((s) => s - 1);
  }

  async submit() {
    if (!this.formStep4.valid) {
      this.snackBar.open(this.translate.instant('buy.step4.acceptTermsError'), 'X', {
        duration: 2500,
      });
      return;
    }
    if (this.awaitingResponse() || this.availableVouchers() <= 0) {
      return;
    }
    this.awaitingResponse.set(true);
    const email = this.formStep2.value.email;
    let amount =
      this.voucherGroup().voucherPrice === 0
        ? this.formStep1.value.amount * this.formStep1.value.value
        : this.formStep1.value.amount * this.voucherGroup().voucherPrice;
    if (this.voucherGroup().administrationCost) {
      amount = amount + this.voucherGroup().administrationCost;
    }
    if (this.formStep1.value.giftWrapping && this.voucherGroup().sellType === 'physical') {
      amount = amount + this.voucherGroup().giftWrappingPrice * this.formStep1.value.amount;
    }
    const postal = this.formStep2.value.postal.replace(/\s/g, '').toUpperCase();
    const name = this.formStep2.value.name;
    const phone = this.formStep2.value.phone;
    const city = this.formStep2.getRawValue().city;
    const street = this.formStep2.getRawValue().street;
    const houseNumber = this.formStep2.value.houseNumber;
    const houseNumberAddition = this.formStep2.value.houseNumberAddition;

    const address = `${street} ${houseNumber} ${houseNumberAddition}`;

    const companyName = this.formStep2.value.companyName;
    // getRawValue gets value even if input is disabled
    const deliveryStreet = this.formStep2.getRawValue().deliveryStreet;
    const deliveryHouseNumber = this.formStep2.getRawValue().deliveryHouseNumber;
    const deliveryHouseNumberAddition = this.formStep2.getRawValue().deliveryHouseNumberAddition;
    const deliveryPostal = this.formStep2
      .getRawValue()
      .deliveryPostal.replace(/\s+/g, '')
      .toUpperCase();
    const deliveryCity = this.formStep2.getRawValue().deliveryCity;
    const deliveryAddress = `${deliveryStreet} ${deliveryHouseNumber} ${deliveryHouseNumberAddition}`;

    const personalMessage = this.formStep3.value.personalMessage;
    const order: Order = {
      amount: this.formStep1.value.amount,
      price:
        this.voucherGroup().voucherPrice === 0
          ? this.formStep1.value.value
          : this.voucherGroup().voucherPrice,
      totalPrice: amount,
      status: 'reserved',
      type: this.voucherGroup().sellType,
      phone: phone,
      voucherGroupId: this.voucherGroupId(),
      customerEmail: email,
      customerName: name,
      customerPostal: postal,
      customerAddress: address,
      customerCity: city,
      voucherGroupName: this.voucherGroup().name,
      packingSlipPersonalMessage: personalMessage,
      deliveryAddress: deliveryAddress,
      deliveryCity: deliveryCity,
      deliveryPostal: deliveryPostal,
      administrationCost: this.voucherGroup().administrationCost ?? 0,
    };
    if (this.voucherGroup().sellType === 'physical') {
      order.useGiftWrapping = this.formStep1.value.giftWrapping;
      if (order.useGiftWrapping) {
        order.giftWrappingPrice = this.voucherGroup().giftWrappingPrice;
      }
    }
    if (companyName) {
      order.companyName = companyName;
    }
    console.log('ORDER:', order);

    try {
      const recaptchaToken =
        await this.publicFunctionsService.getRecaptchaToken('ideal_init_transaction');
      const callable = httpsCallable(this.functions, 'idealInitTransaction', {
        timeout: 60000,
      });
      const result = await callable({
        townshipId: this.townshipId(),
        amount: amount,
        // orderId: newOrder.id,
        order: order,
        voucherGroupId: this.voucherGroupId(),
        recaptchaToken,
      });
      console.log('Transaction result:', result);
      if (result.data['status'] === 200) {
        this.document.location.href = result.data['redirectUrl'];
      } else {
        this.awaitingResponse.set(false);
        this.getTranslation(result.data['status']);
      }
    } catch (error) {
      console.error('Error initializing transaction:', error);
      this.awaitingResponse.set(false);
      this.getTranslation('something-went-wrong');
    }
  }

  getTranslation(objName): void {
    this.translate.get(`snackbar-messages.${objName}`).subscribe((res: string) => {
      const message = res.includes('snackbar-messages.')
        ? this.translate.instant('snackbar-messages.something-went-wrong')
        : res;
      this.snackBar.open(message, '', {
        duration: 12000,
      });
      return message;
    });
  }
}
